import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Shield } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      // Lesson 39: post-login redirect goes to /app (the dashboard).
      // / is the public landing page for unauthenticated visitors.
      navigate("/app");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.message === "account temporarily locked") {
          setError(t("login.locked"));
        } else if (err.status === 401) {
          setError(t("login.invalidCredentials"));
        } else {
          setError(t("login.generalError"));
        }
      } else {
        setError(t("login.generalError"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="px-6 py-3 flex items-center justify-between border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Shield className="text-teal-400 size-5" />
          <span className="text-teal-400 font-semibold">{t("appName")}</span>
        </div>
        <LanguageSwitcher />
      </header>
      <main className="flex-1 flex items-center justify-center px-4">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm rounded-xl border border-slate-800 bg-slate-900/60 p-6 space-y-4"
          data-testid="login-form"
        >
          <div className="text-center space-y-1">
            <h1 className="text-xl font-semibold text-slate-100">{t("login.title")}</h1>
            <p className="text-sm text-slate-400">{t("login.subtitle")}</p>
          </div>
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs text-slate-400 uppercase tracking-wide">
                {t("login.email")}
              </span>
              <input
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.emailPlaceholder")}
                data-testid="login-email"
                className="mt-1 w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-400 uppercase tracking-wide">
                {t("login.password")}
              </span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("login.passwordPlaceholder")}
                data-testid="login-password"
                className="mt-1 w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
              />
            </label>
          </div>
          {error && (
            <div
              className="text-sm text-rose-400 border border-rose-900/50 bg-rose-950/30 rounded px-3 py-2"
              data-testid="login-error"
              role="alert"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="w-full rounded-md bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold py-2 disabled:opacity-50"
          >
            {submitting ? t("login.submitting") : t("login.submit")}
          </button>
        </form>
      </main>
    </div>
  );
}
